import React from 'react';
import { Link } from 'react-router-dom';
import DataTable from 'shared/components/table/DataTable';
import { searchBuildings } from 'shared/api/buildings';
import { dateString } from 'shared/utils';
import { Title } from 'shared/components/abstract/interactables';
import { getUpgrade, searchUpgrades } from 'shared/api/upgrades';
import { getAvControl, searchAvControl } from 'features/building/api/av';
import {
  getRemotePowerManagement,
  searchRemotePowerManagement,
} from 'features/building/api/remote-power-management';
import {
  accessControlEntity, complexEntity, dtvEntity, fireEntity, intrusionEntity, managerEntity,
  surveillanceEntity,
} from 'shared/entityDetails';
import {
  withSelectorSwitch,
  withStaticSelectEditor,
} from 'shared/components/data-display/editors';
import { FilterRedirect } from 'shared/hoc/withFilters';
import { withAsyncSelect } from 'shared/components/data-display/withSelectors';
import { getBuildingPath, viewPath } from 'shared/routes/building';
import { FILTER_DISPLAY_TYPE } from 'shared/constants';
import { AUTH_METHODS, INTERNET_TYPES } from '../../shared/constants/building';
import { formatBuildingUpgrade } from '../../shared/utils/formatters';

const columns = [
  {
    name: 'Name',
    formatter: (row) => <Link to={getBuildingPath(row.id)}>{row.name}</Link>,
    orderBy: 'ip_name',
  },
  {
    name: 'City',
    jsonKey: 'city',
    orderBy: 'ip_city',
  },
  {
    name: 'Service Type',
    jsonKey: 'service_type',
    orderBy: 'ip_internet_service_type',
  },
  {
    name: 'Auth Method',
    jsonKey: 'auth_method',
    orderBy: 'ip_internet_auth_method',
  },
  {
    name: 'Complex',
    formatter: (row) => row.complex.name,
    orderBy: 'ipc_name',
  },
  {
    name: 'Manager',
    formatter: (row) => row.manager.name,
    orderBy: 'ipm_name',
  },
  {
    name: 'Unit Count',
    jsonKey: 'unit_count',
    orderBy: 'unit_count',
  },
  {
    name: 'Last Updated',
    formatter: (row) => dateString(row.updated_at),
    orderBy: 'ip_timestamp',
  },
];

const filters = [
  {
    label: 'Service Type',
    param: 'service_type',
    type: FILTER_DISPLAY_TYPE.control,
    FilterComponent: withStaticSelectEditor(Object.values(INTERNET_TYPES)),
  },
  {
    label: 'Auth Method',
    param: 'auth_method',
    type: FILTER_DISPLAY_TYPE.control,
    FilterComponent: withStaticSelectEditor(Object.values(AUTH_METHODS)),
  },
  {
    label: 'Complex',
    param: 'complex_id',
    type: FILTER_DISPLAY_TYPE.control,
    FilterComponent: withAsyncSelect(complexEntity),
    entity: complexEntity,
  },
  {
    label: 'Manager',
    param: 'manager_id',
    type: FILTER_DISPLAY_TYPE.control,
    FilterComponent: withAsyncSelect(managerEntity),
    entity: managerEntity,
  },
  {
    label: 'More Filters',
    key: 'more-filters',
    type: FILTER_DISPLAY_TYPE.group,
    items: [
      {
        label: 'Access Control',
        param: 'access_control_id',
        type: FILTER_DISPLAY_TYPE.control,
        FilterComponent: withAsyncSelect(accessControlEntity),
        entity: accessControlEntity,
      },
      {
        label: 'AV Control',
        param: 'control_system_id',
        type: FILTER_DISPLAY_TYPE.control,
        FilterComponent: withAsyncSelect({
          name: 'AV Control',
          getRequest: getAvControl,
          searchRequest: searchAvControl,
        }),
        entity: {
          name: 'AV Control',
          getRequest: getAvControl,
        },
      },
      {
        label: 'DTV Package',
        param: 'dtv_p_id',
        type: FILTER_DISPLAY_TYPE.control,
        FilterComponent: withAsyncSelect(dtvEntity),
        entity: dtvEntity,
      },
      {
        label: 'Fire Monitor',
        param: 'fire_monitor_id',
        type: FILTER_DISPLAY_TYPE.control,
        FilterComponent: withAsyncSelect(fireEntity),
        entity: fireEntity,
      },
      {
        label: 'Intrusion Monitor',
        param: 'intrusion_monitor_id',
        type: FILTER_DISPLAY_TYPE.control,
        FilterComponent: withAsyncSelect(intrusionEntity),
        entity: intrusionEntity,
      },
      {
        label: 'Remote Pwr Mgmt',
        param: 'remote_power_management_id',
        type: FILTER_DISPLAY_TYPE.control,
        FilterComponent: withAsyncSelect({
          name: 'Remote Pwr Mgmt',
          getRequest: getRemotePowerManagement,
          searchRequest: searchRemotePowerManagement,
        }),
        entity: {
          name: 'Remote Pwr Mgmt',
          getRequest: getRemotePowerManagement,
        },
      },
      {
        label: 'Surveillance Monitor',
        param: 'surveillance_id',
        type: FILTER_DISPLAY_TYPE.control,
        FilterComponent: withAsyncSelect(surveillanceEntity),
        entity: surveillanceEntity,
      },
      {
        label: 'Upgrade Plan',
        param: 'plan_id',
        type: FILTER_DISPLAY_TYPE.control,
        FilterComponent: withAsyncSelect({
          name: 'Upgrade',
          getRequest: getUpgrade,
          searchRequest: searchUpgrades,
          getOptionLabel: formatBuildingUpgrade,
        }),
        entity: {
          name: 'Upgrade',
          getRequest: getUpgrade,
          searchRequest: searchUpgrades,
          getOptionLabel: formatBuildingUpgrade,
        },
      },
    ],
  },
  {
    label: 'Include Archived?',
    param: 'include_archived',
    type: FILTER_DISPLAY_TYPE.control,
    FilterComponent: withSelectorSwitch(),
  },
];

function getRowClassName(row) {
  return row.archived_at ? 'archived table-row' : 'table-row';
}

/**
 * @param {string} filterKey - Key for filtering
 * @param {string} savedFilters - Saved filter parameters
 * @param {string} path - Current path
 */
export default function BuildingSearch({
  filterKey,
  savedFilters,
  path,
}) {
  if (!FilterRedirect(path, savedFilters)) {
    return (
      <>
        <Title title="Buildings" titleLink={viewPath} />
        <DataTable
          searchRequest={searchBuildings}
          getRowClassName={getRowClassName}
          rowId="id"
          columns={columns}
          filters={filters}
          filterKey={filterKey}
          className="table-height-large"
        />
      </>
    );
  }
}
